<template>
  <div v-if="letting" class="rentalObjectAd">
    <!-- Mark as rented modal -->
    <hl-Modal
      v-if="isUnpublishModalVisible"
      iconClass="budicon-alert-sign text-blue"
      title="Ønsker du å avslutte annonsen?"
      class="text-center"
    >
      <p>
        Ved å gå videre vil din annonse fjernes fra Husleie.no sin markedsplass og/eller FINN.no. Den vil ikke lenger
        være synlig for interessenter.
      </p>
      <div class="d-flex justify-content-center align-items-center mt-3">
        <hl-Button variant="primary" :disabled="isUnpublishingAd" @click="unpublishAd">
          <div class="d-flex align-items-center">
            <hl-LoaderDots v-if="isUnpublishingAd" variant="white" class="mr-2" />
            Avslutt annonse
          </div>
        </hl-Button>
        <hl-Button variant="danger" class="ml-2" @click="isUnpublishModalVisible = false"> Avbryt </hl-Button>
      </div>
    </hl-Modal>
    <!-- / Mark as rented Modal -->

    <!-- Bulk Viewing Modal -->
    <hl-Modal
      v-if="isInviteToViewingModalVisible"
      iconClass="budicon-view text-primary"
      title="Inviter til visning"
      class="text-center"
    >
      <div>
        <p>
          For å invitere de valgte interessentene til visning må du skrive inn et tidspunkt og eventuell info om
          visningen under.
        </p>
        <p class="mb-3">
          Når du klikker på “Inviter” vil det sendes en invitasjon per e-st til følgende interessenter:
        </p>
        <hl-Tag
          v-for="(checkedProspect, index) in getCheckedProspectNames"
          :key="index"
          variation="primary"
          class="d-inline-block m-2"
        >
          {{ checkedProspect }}
        </hl-Tag>

        <div class="mt-3 mb-5">
          <BulkProspectViewingForm @updated="updateNewViewingDetails" />
        </div>

        <button class="btn btn-primary mx-2" @click="inviteToViewingAndClose">Inviter</button>
        <button class="btn btn-outline-secondary mx-2" @click="isInviteToViewingModalVisible = false">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- / Bulk Viewing Modal -->

    <!-- Decline Prospect Modal -->
    <hl-Modal
      v-if="isDeclineModalVisible"
      iconClass="budicon-sad-chat text-danger"
      title="Send avslag til valgte interessenter"
      class="text-center"
    >
      <div>
        <div class="mb-5">
          <p>
            Klikk på “Avslag” og det sendes en e-post til interessentene under hvor det informeres om at de ikke er
            aktuell som leietaker for boligen.
          </p>
          <hl-Tag
            v-for="(checkedProspect, index) in getCheckedProspectNames"
            :key="index"
            variation="danger"
            class="d-inline-block m-2"
          >
            {{ checkedProspect }}
          </hl-Tag>
        </div>

        <button class="btn btn-danger mx-2" @click="declineAndClose">Avslag</button>
        <button class="btn btn-outline-secondary mx-2" @click="isDeclineModalVisible = false">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- / Decline Prospect Modal -->

    <!-- Compact design version -->
    <div v-if="isCompactDesign" class="rentalObjectAd__ad">
      <div class="rentalObjectAd__ad__actionsMenu mb-3">
        <ActionsMenu :menuItems="actionMenuItems" iconClass="budicon-kebab-ui" />
      </div>

      <div class="rentalObjectAd__ad__flex">
        <a
          :href="isMarketPlaceAdActive ? titleLink : undefined"
          target="_blank"
          rel="noopener"
          class="rentalObjectAd__ad__flex__imageLink"
        >
          <img v-if="imgUri" :src="(env === 'production' ? '' : 'https://test.no.0x484c.com') + imgUri" />
          <img v-else src="@modules/husleie-framework/img/graphic-elements/maps/map_marker.png" />
        </a>

        <div class="rentalObjectAd__ad__flex__content">
          <div class="rentalObjectAd__ad__flex__content__tags">
            <hl-Tag size="sm" :variant="adStatusBadge.variant" isLargeBorderRadius>
              {{ adStatusBadge.text }}
            </hl-Tag>

            <hl-Tag v-if="getProspects.length" size="sm" variant="white" shouldShowDot isLargeBorderRadius>
              {{ getProspects.length }} søker(e)
            </hl-Tag>
          </div>

          <div class="rentalObjectAd__ad__flex__content__titleRow">
            <div>
              <a
                :href="isMarketPlaceAdActive ? titleLink : undefined"
                target="_blank"
                rel="noopener"
                class="rentalObjectAd__ad__flex__content__titleRow__marketPlaceLink"
              >
                <h5 class="mt-3">
                  {{ title }}
                </h5>
              </a>

              <p class="text-gray2">
                {{ letting.rentalObject.address.street }}, {{ letting.rentalObject.address.postalZipCode }}
                {{ letting.rentalObject.address.postalArea }}
              </p>
            </div>

            <div class="rentalObjectAd__ad__flex__content__titleRow__buttons">
              <hl-Button
                v-if="!isRentalObjectCommercial"
                variant="secondary"
                outline
                :href="!isAdExpired ? pubomaticAppEditAdLink : undefined"
                target="_blank"
                rel="noopener"
                :title="
                  isAdExpired
                    ? 'Om du ønsker å republisere en tidligere annonse må du velge opprett ny annonse. Da vil du få valg om å gjenbruke tidligere annonser.'
                    : undefined
                "
                :disabled="isAdExpired"
                class="font-weight-bold"
              >
                Rediger
              </hl-Button>

              <router-link
                :to="{
                  name: 'rentalObject_ads',
                  params: {
                    landlordId,
                    rentalObjectId: letting.status.rentalObjectId,
                  },
                  query: {
                    filter: adQueryFilter,
                  },
                }"
                v-slot="{ href, navigate }"
              >
                <hl-Button :href="href" class="font-weight-bold" @click="navigate"> Se søkere </hl-Button>
              </router-link>

              <ActionsMenu :menuItems="actionMenuItems" iconClass="budicon-kebab-ui" />
            </div>
          </div>

          <div class="rentalObjectAd__ad__flex__content__publishBadges">
            <a v-for="badge in publishBadges" :key="badge.text" :href="badge.url" target="_blank" rel="noopener">
              <hl-Tag size="sm" :variant="badge.variant" shouldShowDot>
                {{ badge.text }}
              </hl-Tag>
            </a>
          </div>

          <div class="rentalObjectAd__ad__flex__content__buttons">
            <hl-Button
              v-if="!isRentalObjectCommercial"
              variant="secondary"
              outline
              :href="!isAdExpired ? pubomaticAppEditAdLink : undefined"
              target="_blank"
              rel="noopener"
              :title="
                isAdExpired
                  ? 'Om du ønsker å republisere en tidligere annonse må du velge opprett ny annonse. Da vil du få valg om å gjenbruke tidligere annonser.'
                  : undefined
              "
              :disabled="isAdExpired"
              class="font-weight-bold"
            >
              Rediger
            </hl-Button>

            <router-link
              :to="{
                name: 'rentalObject_ads',
                params: {
                  landlordId,
                  rentalObjectId: letting.status.rentalObjectId,
                },
                query: {
                  filter: adQueryFilter,
                },
              }"
              v-slot="{ href, navigate }"
            >
              <hl-Button :href="href" class="font-weight-bold" @click="navigate"> Se søkere </hl-Button>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Full design version -->
    <template v-else>
      <div class="rentalObjectAd__ad">
        <div class="rentalObjectAd__ad__actionsMenu mb-3">
          <ActionsMenu :menuItems="actionMenuItems" iconClass="budicon-kebab-ui" />
        </div>

        <div class="rentalObjectAd__ad__flex">
          <a
            :href="isMarketPlaceAdActive ? titleLink : undefined"
            target="_blank"
            rel="noopener"
            class="rentalObjectAd__ad__flex__imageLink"
          >
            <img v-if="imgUri" :src="(env === 'production' ? '' : 'https://test.no.0x484c.com') + imgUri" />
            <img v-else src="@modules/husleie-framework/img/graphic-elements/maps/map_marker.png" />
          </a>

          <div class="rentalObjectAd__ad__flex__content">
            <hl-Tag size="sm" :variant="adStatusBadge.variant" isLargeBorderRadius>
              {{ adStatusBadge.text }}
            </hl-Tag>

            <div class="rentalObjectAd__ad__flex__content__titleRow">
              <div>
                <a
                  :href="isMarketPlaceAdActive ? titleLink : undefined"
                  target="_blank"
                  rel="noopener"
                  class="rentalObjectAd__ad__flex__content__titleRow__marketPlaceLink"
                >
                  <h5 class="mt-3">
                    {{ title }}
                  </h5>
                </a>

                <p class="text-gray2">
                  {{ letting.rentalObject.address.street }}, {{ letting.rentalObject.address.postalZipCode }}
                  {{ letting.rentalObject.address.postalArea }}
                </p>
              </div>

              <div class="rentalObjectAd__ad__flex__content__titleRow__buttons">
                <hl-Button
                  v-if="!isRentalObjectCommercial"
                  variant="secondary"
                  outline
                  :href="!isAdExpired ? pubomaticAppEditAdLink : undefined"
                  target="_blank"
                  rel="noopener"
                  :title="
                    isAdExpired
                      ? 'Om du ønsker å republisere en tidligere annonse må du velge opprett ny annonse. Da vil du få valg om å gjenbruke tidligere annonser.'
                      : undefined
                  "
                  :disabled="isAdExpired"
                  class="font-weight-bold"
                >
                  Rediger
                </hl-Button>

                <ActionsMenu :menuItems="actionMenuItems" iconClass="budicon-kebab-ui" />
              </div>
            </div>

            <div class="rentalObjectAd__ad__flex__content__publishBadges">
              <a v-for="badge in publishBadges" :key="badge.text" :href="badge.url" target="_blank" rel="noopener">
                <hl-Tag size="sm" :variant="badge.variant" shouldShowDot>
                  {{ badge.text }}
                </hl-Tag>
              </a>
            </div>

            <div class="rentalObjectAd__ad__flex__content__buttons">
              <hl-Button
                v-if="!isRentalObjectCommercial"
                variant="secondary"
                outline
                :href="!isAdExpired ? pubomaticAppEditAdLink : undefined"
                target="_blank"
                rel="noopener"
                :title="
                  isAdExpired
                    ? 'Om du ønsker å republisere en tidligere annonse må du velge opprett ny annonse. Da vil du få valg om å gjenbruke tidligere annonser.'
                    : undefined
                "
                :disabled="isAdExpired"
                class="font-weight-bold"
              >
                Rediger
              </hl-Button>

              <hl-Button
                v-if="publisherData[$options.PUBLISHER.finn] === undefined"
                :outline="true"
                variant="extra"
                @click="$emit('publish-to-finn', letting.id)"
              >
                <i class="budicon-announcement"></i>
                Publiser til FINN
              </hl-Button>
            </div>
          </div>
        </div>

        <div class="rentalObjectAd__ad__candidatesSelection mt-4">
          <div class="d-flex flex-wrap">
            <h4 class="mt-1 mr-3">Leietakersøknader</h4>

            <hl-Tag v-if="newCandidates" variant="white" shouldShowDot>
              <template v-if="newCandidates === 1"> {{ newCandidates }} ny søker </template>
              <template v-else> {{ newCandidates }} nye søkere </template>
            </hl-Tag>
          </div>

          <div class="rentalObjectAd__ad__candidatesSelection__buttons">
            <template v-if="isCandidateToggledInFilter">
              <hl-Button :outline="true" variant="secondary" @click="isInviteToViewingModalVisible = true">
                <strong>Visning</strong>
              </hl-Button>

              <hl-Button :outline="true" variant="danger" @click="isDeclineModalVisible = true">
                <strong>Avslag</strong>
              </hl-Button>
            </template>

            <hl-Button
              href="/app/tenant-profiles"
              target="_blank"
              rel="noopener"
              variant="secondary"
            >
              <strong>Finn leietakere</strong>
            </hl-Button>

            <hl-Button :outline="true" variant="extra" @click="toggleNewCandidateModal(iM.id)">
              <Icon :name="IconName.InterfacePlus" :size="IconSize.Small" class="text-primary" />
              <strong class="ml-2">Legg til søker</strong>
            </hl-Button>
          </div>
        </div>

        <template v-if="getProspects.length">
          <div class="rentalObjectAd__ad__filterSection">
            <p class="text-gray2 mt-1">Administrer søkere og deres status</p>

            <hr />

            <div class="rentalObjectAd__ad__filterSection__grid">
              <div>
                <p class="mb-1">Søk etter navn</p>
                <input type="text" placeholder="Søk" v-model="filter.name" />
              </div>
              <div>
                <p class="mb-1">Status</p>
                <select v-model="filter.status">
                  <option v-for="option in candidateStatusOptions" :value="option.value" :key="option.value">
                    {{ option.name }}
                  </option>
                </select>
              </div>
              <div>
                <p class="mb-1">Kredittsjekk</p>
                <select v-model="filter.creditCheck">
                  <option v-for="option in creditCheckOptions" :value="option.value" :key="option.value">
                    {{ option.name }}
                  </option>
                </select>
              </div>
              <div>
                <p class="mb-1">Rangering</p>
                <select v-model="filter.rank">
                  <option v-for="option in rankingOptions" :value="option.value" :key="option.value">
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </div>
            <hr />
          </div>

          <div v-if="filteredProspects.length" class="rentalObjectAd__ad__candidateCardsSection">
            <div class="d-flex align-items-center ml-1">
              <input type="checkbox" :checked="isAllCandidatesSelected" @input="toggleAllCandidates" />
              <span class="ml-2"> {{ filteredProspects.length }} søker(e) </span>
            </div>

            <div class="rentalObjectAd__ad__candidateCardsSection__grid mt-3">
              <div v-for="candidate in filteredProspects" :key="candidate.id">
                <CandidateCard
                  :landlord-id="parseInt(landlordId)"
                  :rental-object-id="letting.status.rentalObjectId"
                  :iMId="iM.id"
                  :candidate-id="candidate.id"
                  :name="candidate.name"
                  :date="candidate.created"
                  :candidate-status-variant="candidate.statusObject.tagVariant"
                  :candidate-status-text="candidate.statusObject.tagText"
                  :is-selected="candidateToggling[candidate.id]"
                  :rank="candidate.rank || 0"
                  :unread-messages="candidate.unreadMessages"
                  :credit-check-status-variant="candidate.creditCheckObject.tagVariant"
                  :credit-check-status-icon="candidate.creditCheckObject.tagIconName"
                  :credit-check-status-text="candidate.creditCheckObject.tagText"
                  :prospectDetailsRouteName="prospectDetailsRouteName"
                  :avatar-id="candidate.avatar_id"
                  @toggleCandidate="toggleCandidate"
                  @goToProspectDetails="goToProspectDetails"
                  @updateProspectScore="
                    data =>
                      updateProspectScore({
                        landlordId,
                        prospectId: data.candidateId,
                        rank: data.rank,
                        iMId: iM.id,
                      })
                  "
                />
              </div>
            </div>
          </div>

          <div v-if="filteredProspects.length" class="rentalObjectAd__ad__candidateSection">
            <div class="rentalObjectAd__ad__candidateSection__grid -header">
              <div class="d-flex">
                <input type="checkbox" :checked="isAllCandidatesSelected" @input="toggleAllCandidates" />
                <span class="ml-2"> {{ filteredProspects.length }} søker(e) </span>
              </div>

              <div class="d-flex align-items-center ml-1">
                Dato
                <i
                  class="pointer"
                  :class="[
                    candidatesSorting.field === $options.CANDIDATES_SORT.date && !candidatesSorting.isOrderDesc
                      ? 'budicon-arrow-top'
                      : 'budicon-arrow-bottom',
                  ]"
                  @click="toggleCandidatesSorting($options.CANDIDATES_SORT.date)"
                />
              </div>

              <div class="d-flex align-items-center ml-1">
                Status
                <Tooltip>
                  <template v-slot:trigger>
                    <span class="rentalObjectAd__ad__candidateSection__grid__questionMark ml-2"> ? </span>
                  </template>

                  <div
                    v-for="(item, index) in Object.values(candidateStatusObjects)"
                    :key="index"
                    class="rentalObjectAd__ad__candidateSection__grid__tooltipContent"
                  >
                    <hl-Tag :variant="item.tagVariant" shouldShowDot>
                      <span class="rentalObjectAd__ad__candidateSection__grid__tooltipContent__tagText">
                        {{ item.tagText }}
                      </span>
                    </hl-Tag>

                    {{ item.helpText }}
                  </div>
                </Tooltip>
              </div>

              <div class="d-flex align-items-center ml-1">
                Rangering
                <Tooltip>
                  <template v-slot:trigger>
                    <span class="rentalObjectAd__ad__candidateSection__grid__questionMark ml-2"> ? </span>
                  </template>

                  {{ ratingHelpText }}
                </Tooltip>
                <i
                  class="pointer"
                  :class="[
                    candidatesSorting.field === $options.CANDIDATES_SORT.rating && !candidatesSorting.isOrderDesc
                      ? 'budicon-arrow-top'
                      : 'budicon-arrow-bottom',
                  ]"
                  @click="toggleCandidatesSorting($options.CANDIDATES_SORT.rating)"
                />
              </div>

              <div class="d-flex align-items-center ml-1">
                Kredittsjekk
                <Tooltip>
                  <template v-slot:trigger>
                    <span class="rentalObjectAd__ad__candidateSection__grid__questionMark ml-2"> ? </span>
                  </template>

                  <div
                    v-for="(item, index) in Object.values(creditCheckObjects)"
                    :key="index"
                    class="rentalObjectAd__ad__candidateSection__grid__tooltipContent"
                  >
                    <hl-Tag :variant="item.tagVariant">
                      <Icon :name="item.tagIconName" />
                      <span class="rentalObjectAd__ad__candidateSection__grid__tooltipContent__tagText">
                        {{ item.tagText }}
                      </span>
                    </hl-Tag>

                    {{ item.helpText }}
                  </div>
                </Tooltip>
              </div>
            </div>

            <div v-for="candidate in filteredProspects" :key="candidate.id">
              <hr />

              <div class="rentalObjectAd__ad__candidateSection__grid -content">
                <div class="d-flex align-items-center">
                  <input type="checkbox" v-model="candidateToggling[candidate.id]" class="mr-1" />

                  <router-link
                    :to="{
                      name: prospectDetailsRouteName,
                      params: {
                        iMId: iM.id,
                        prospectId: candidate.id,
                      },
                    }"
                    class="d-flex align-items-center"
                  >
                    <span v-if="candidate.unreadMessages" class="rentalObjectAd__ad__dot ml-2" />

                    <CandidateAvatar
                      :avatar-id="candidate.avatar_id"
                      variant="circle"
                      has-border
                      class="ml-2"
                    />

                    <span class="ml-2">
                      {{ candidate.name }}
                    </span>
                  </router-link>
                </div>

                <hl-Date :date="candidate.created" class="mt-1" />

                <hl-Tag
                  class="rentalObjectAd__ad__candidateSection__grid__tag"
                  :variant="candidate.statusObject.tagVariant"
                  isLargeBorderRadius
                  shouldShowDot
                >
                  {{ candidate.statusObject.tagText }}
                </hl-Tag>

                <hl-RatingStars
                  :value="candidate.rank"
                  @newScore="
                    updateProspectScore({
                      landlordId,
                      prospectId: candidate.id,
                      rank: $event,
                      iMId: iM.id,
                    })
                  "
                />

                <hl-Tag
                  class="rentalObjectAd__ad__candidateSection__grid__tag"
                  :variant="candidate.creditCheckObject.tagVariant"
                  isLargeBorderRadius
                >
                  <div class="d-flex align-items-center">
                    <Icon
                      v-if="candidate.creditCheckObject.tagIconName"
                      :name="candidate.creditCheckObject.tagIconName"
                      class="mr-2"
                    />
                    {{ candidate.creditCheckObject.tagText }}
                  </div>
                </hl-Tag>

                <div class="d-flex justify-content-end">
                  <router-link
                    :to="{
                      name: prospectDetailsRouteName,
                      params: {
                        landlordId,
                        rentalObjectId: letting.status.rentalObjectId,
                        iMId: iM.id,
                        prospectId: candidate.id,
                      },
                    }"
                    v-slot="{ href, navigate }"
                    custom
                  >
                    <hl-Button :href="href" variant="extra" size="sm" outline @click="navigate">
                      <div class="d-flex align-items-center ml-1">
                        <strong>Profil</strong>
                        <strong>
                          <i class="budicon-chevron-right h5" />
                        </strong>
                      </div>
                    </hl-Button>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <hr />
          <strong>Du har ingen søkere</strong>

          <p class="mt-1">Når du får søkere på boligen din vil de vises her.</p>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import * as moment from 'moment';
import Tag from '@modules/husleie-framework/src/components/forms/inputs/tags/Tag.vue';
import InfoCard from '@modules/husleie-framework/src/components/layout/cards/InfoCard.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Hldate from '@modules/husleie-framework/src/components/time/Date.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots';
import ModalV2 from '@modules/husleie-framework/src/components/modals/ModalV2.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import BulkProspectViewingForm from '@/components/detailedRentalObject/interestManagement/forms/viewings/BulkProspectViewingForm';
import Checkbox from '@modules/husleie-framework/src/components/forms/inputs/checkbox/Checkbox.vue';
import Icon, { IconName, IconSize } from '@modules/design-system/components/Icon';
import CandidateCard from '@/components/CandidateCard.vue';
import ActionsMenu from '@/components/UI/ActionsMenu';
import Tooltip from '@/components/Tooltip.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import CandidateAvatar from '@/components/CandidateAvatar.vue';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

const PUBLISHER = {
  marketplace: 'marketplace',
  finn: 'finn',
};

export default Vue.extend({
  components: {
    'hl-Tag': Tag,
    'hl-InfoCard': InfoCard,
    'hl-Button': Button,
    'hl-Currency': Currency,
    'hl-Date': Hldate,
    'hl-ModalV2': ModalV2,
    'hl-Checkbox': Checkbox,
    'hl-LoaderDots': LoaderDots,
    ActionsMenu,
    CandidateCard,
    BulkProspectViewingForm,
    Tooltip,
    Icon,
    CandidateAvatar,
  },
  LETTING_STATUS: {
    active: 'active',
    pending: 'pending',
    expired: 'expired',
  },
  PUBLISHER,
  PUBLISHER_NAME: {
    [PUBLISHER.marketplace]: 'Husleie.no',
    [PUBLISHER.finn]: 'FINN',
  },
  PUBLISHER_STATUS: {
    uploaded: 'uploaded',
    active: 'active',
    pending: 'pending',
    stopped: 'stopped',
    failed: 'failed',
    expired: 'expired',
  },
  CANDIDATES_SORT: {
    date: 'date',
    rating: 'rating',
  },
  CANDIDATE_STATUS: {
    applicant: 'applicant',
    invited: 'invited',
    withdrawn: 'withdrawn',
    rejected: 'rejected',
    contracted: 'contracted',
  },
  CREDIT_CHECK_STATUS: {
    notInitiated: 'not_initiated',
    candidateInvited: 'candidate_invited',
    candidateRejected: 'candidate_rejected',
    positive: 'positive',
    negative: 'negative',
  },
  props: {
    iM: {
      type: Object,
      required: true,
    },
    landlordId: {
      type: [Number, String],
      required: true,
    },
    letting: {
      type: Object,
      required: true,
    },
    isCompactDesign: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      env: process.env.NODE_ENV,
      isUnpublishModalVisible: false,
      isInviteToViewingModalVisible: false,
      isDeclineModalVisible: false,
      newViewing: {},
      candidateToggling: {},
      candidatesSorting: {
        field: 'date',
        isOrderDesc: true,
      },
      isUnpublishingAd: false,
      filter: {
        name: undefined,
        status: 'all',
        creditCheck: 'all',
        rank: 'all',
      },
      IconName,
      IconSize,
    };
  },
  computed: {
    ...mapGetters(['getLettingById', 'getProspectList', 'getProspectDetails']),

    selectedCandidates() {
      const candidates = [];

      for (const key in this.candidateToggling) {
        if (this.candidateToggling[key]) {
          candidates.push(key);
        }
      }

      return candidates;
    },

    isAllCandidatesSelected() {
      return this.selectedCandidates.length === this.filteredProspects.length;
    },

    isCandidateToggledInFilter() {
      for (const candidate of this.filteredProspects) {
        if (this.selectedCandidates.includes(String(candidate.id))) {
          return true;
        }
      }

      return false;
    },

    newCandidates() {
      const candidates = this.getProspects.filter(candidate => moment().diff(candidate.created, 'days') <= 1);

      return candidates.length;
    },

    actionMenuItems() {
      return [
        {
          name: 'Publiser til FINN',
          action: () => {
            this.$emit('publish-to-finn', this.letting.id);
          },
        },
        this.isMarketPlaceAdActive
          ? {
              name: 'Kopier lenke til annonse',
              action: () => {
                navigator.clipboard.writeText(this.titleLink);
              },
            }
          : undefined,
        {
          name: 'Del på Facebook (kommer snart)',
          disabled: true,
        },
        {
          name: 'Se statistikk (kommer snart)',
          disabled: true,
        },
        {
          name: 'Avslutt annonse',
          action: () => {
            this.isUnpublishModalVisible = true;
          },
        },
      ].filter(item => item);
    },

    publisherData() {
      return this.letting.status.publishers.reduce((acc, publisher) => {
        let toDate;
        if (publisher.name === PUBLISHER.finn) {
          toDate = publisher.to_date;
        } else if (publisher.name === PUBLISHER.marketplace) {
          toDate = moment(publisher.meta.last_upload).add(30, 'd').toISOString();
        }

        acc[publisher.name] = {
          status: publisher.status,
          toDate,
          url: publisher?.meta?.url,
        };

        return acc;
      }, {});
    },

    adStatusBadge() {
      if (this.letting.status.status === this.$options.LETTING_STATUS.pending) {
        return {
          text: 'Venter på godkjenning',
          variant: 'error',
        };
      }

      if (this.letting.status.status === this.$options.LETTING_STATUS.expired) {
        return {
          text: 'Utløpt',
          variant: 'error',
        };
      }

      if (this.letting.status.status === this.$options.LETTING_STATUS.active) {
        return {
          text: 'Aktiv',
          variant: 'primary',
        };
      }

      return {
        text: 'Ukjent',
        variant: 'error',
      };
    },

    publishBadges() {
      if (this.letting.status.status === this.$options.LETTING_STATUS.active) {
        return Object.keys(this.$options.PUBLISHER)
          .map(publisher => this.getPublisherBadge(publisher))
          .filter(data => data !== undefined);
      }

      return [];
    },

    candidates() {
      return this.iM.candidates.total;
    },

    imgUri() {
      if (this.letting.media == null || this.letting.media.length === 0) return;
      return this.letting.media[0].mediaLinks[0].url;
    },

    title() {
      return this.letting.content[0].sectionHeading;
    },

    isMarketPlaceAdActive() {
      return this.publisherData[this.$options.PUBLISHER.marketplace]?.status === 'active';
    },

    titleLink() {
      return this.publisherData[this.$options.PUBLISHER.marketplace]?.url;
    },

    getProspects() {
      return (this.getProspectList(this.iM.id) ?? []).map(item => ({
        ...item,
        unreadMessages: this.countUnreadMessages(item),
        statusObject: this.candidateStatusObjects[item.status],
        creditCheckObject: this.initCreditCheckObject(item.credit_check),
      }));
    },
    filteredProspects() {
      const { name, status, creditCheck, rank } = this.filter;

      const filtered = this.getProspects.filter(candidate => {
        if (name && !candidate.name.toLowerCase().includes(name.toLowerCase())) {
          return false;
        }

        if (status && status !== 'all' && status !== candidate.status) {
          return false;
        }

        if (creditCheck && creditCheck !== 'all' && creditCheck !== candidate.creditCheckObject.status) {
          return false;
        }

        if (rank && rank !== 'all' && parseInt(rank) !== candidate.rank) {
          return false;
        }

        return true;
      });

      return filtered.sort((pa, pb) => {
        let aSortValue, bSortValue;

        switch (this.candidatesSorting.field) {
          case this.$options.CANDIDATES_SORT.date:
            aSortValue = new Date(pa.created);
            bSortValue = new Date(pb.created);
            break;
          case this.$options.CANDIDATES_SORT.rating:
            aSortValue = pa.rank;
            bSortValue = pb.rank;
            break;
        }

        if (this.candidatesSorting.isOrderDesc) {
          return aSortValue - bSortValue;
        } else {
          return bSortValue - aSortValue;
        }
      });
    },
    getCheckedProspectNames() {
      return this.selectedCandidates.map(candidateId => {
        return this.getProspectDetails(this.iM.id, candidateId).name;
      });
    },
    candidateStatusObjects() {
      return {
        [this.$options.CANDIDATE_STATUS.applicant]: {
          tagVariant: 'info',
          tagText: 'Venter',
          helpText: 'Søknaden er mottatt og venter på behandling.',
          optionValue: this.$options.CANDIDATE_STATUS.applicant,
          optionName: 'Venter',
        },
        [this.$options.CANDIDATE_STATUS.invited]: {
          tagVariant: 'info',
          tagText: 'Invitert',
          helpText: 'Søkeren er invitert til visning.',
          optionValue: this.$options.CANDIDATE_STATUS.invited,
          optionName: 'Invitert',
        },
        [this.$options.CANDIDATE_STATUS.withdrawn]: {
          tagVariant: 'error',
          tagText: 'Avslått',
          helpText: 'Søkeren har trukket sin søknad.',
          optionValue: this.$options.CANDIDATE_STATUS.withdrawn,
          optionName: 'Avslått',
        },
        [this.$options.CANDIDATE_STATUS.rejected]: {
          tagVariant: 'error',
          tagText: 'Avvist',
          helpText: 'Søknaden har blitt sendt avslag.',
          optionValue: this.$options.CANDIDATE_STATUS.rejected,
          optionName: 'Avvist',
        },
        [this.$options.CANDIDATE_STATUS.contracted]: {
          tagVariant: 'primary',
          tagText: 'Kontrakt signert',
          helpText: 'Søkeren har signert kontrakten.',
          optionValue: this.$options.CANDIDATE_STATUS.contracted,
          optionName: 'Kontrakt sendt',
        },
      };
    },
    candidateStatusOptions() {
      return [
        {
          value: 'all',
          name: 'Alle',
        },
        ...Object.values(this.candidateStatusObjects).map(item => ({
          value: item.optionValue,
          name: item.optionName,
        })),
      ];
    },
    creditCheckObjects() {
      return {
        [this.$options.CREDIT_CHECK_STATUS.notInitiated]: {
          status: this.$options.CREDIT_CHECK_STATUS.notInitiated,
          tagVariant: 'warning',
          tagIconName: IconName.InterfaceInfo,
          tagText: 'Ikke startet',
          helpText: 'Denne søkeren har ikke blitt sendt en invitasjon til å gjennomføre en kredittsjekk.',
          optionValue: this.$options.CREDIT_CHECK_STATUS.notInitiated,
          optionName: 'Ikke startet',
        },
        [this.$options.CREDIT_CHECK_STATUS.candidateInvited]: {
          status: this.$options.CREDIT_CHECK_STATUS.candidateInvited,
          tagVariant: 'warning',
          tagIconName: IconName.InterfaceInfo,
          tagText: 'Invitasjon sendt',
          helpText: 'En invitasjon til kredittsjekk er sendt, men søkeren har ikke fullført kredittsjekken.',
          optionValue: this.$options.CREDIT_CHECK_STATUS.candidateInvited,
          optionName: 'Invitert',
        },
        [this.$options.CREDIT_CHECK_STATUS.candidateRejected]: {
          status: this.$options.CREDIT_CHECK_STATUS.candidateRejected,
          tagVariant: 'warning',
          tagIconName: IconName.InterfaceInfo,
          tagText: 'Avslått',
          helpText: 'Søkeren har valgt å avstå fra å bli kredittsjekket.',
          optionValue: this.$options.CREDIT_CHECK_STATUS.candidateRejected,
          optionName: 'Avslått',
        },
        [this.$options.CREDIT_CHECK_STATUS.negative]: {
          status: this.$options.CREDIT_CHECK_STATUS.negative,
          tagVariant: 'error',
          tagIconName: IconName.InterfaceDislike,
          tagText: 'Negativ',
          helpText:
            'Kredittsjekken er negativ. Dette er en helhetlig vurdering basert på eventuelle betalingsanmerkninger og/eller manglende registrert inntekt i ett av de tre siste årene, eller indikerer en dårlig betalingshistorikk eller høy risiko.',
          optionValue: this.$options.CREDIT_CHECK_STATUS.negative,
          optionName: 'Negativ',
        },
        [this.$options.CREDIT_CHECK_STATUS.positive]: {
          status: this.$options.CREDIT_CHECK_STATUS.positive,
          tagVariant: 'primary',
          tagIconName: IconName.InterfaceVerified,
          tagText: 'Godkjent',
          helpText:
            'Kredittsjekken er godkjent. Dette innebærer at søkeren har hatt inntekt i minst ett av de tre siste årene, og at det ikke foreligger betalingsanmerkninger.',
          optionValue: this.$options.CREDIT_CHECK_STATUS.completed,
          optionName: 'Godkjent',
        },
      };
    },
    creditCheckOptions() {
      return [
        {
          value: 'all',
          name: 'Alle',
        },
        ...Object.values(this.creditCheckObjects).map(item => ({
          value: item.optionValue,
          name: item.optionName,
        })),
      ];
    },
    rankingOptions() {
      return [
        {
          value: 'all',
          name: 'Alle',
        },
        ...[1, 2, 3, 4, 5].map(item => ({
          value: item,
          name: item,
        })),
      ];
    },
    ratingHelpText() {
      return (
        'Stjernerangeringen gir deg som utleier en enkel og oversiktlig måte å evaluere leietakere ' +
        'på. Denne vurderingen er kun synlig for deg og er ment for intern bruk, slik at du ' +
        'lettere kan holde oversikt over tidligere og nåværende leietakere.'
      );
    },
    pubomaticAppLink() {
      return `/app/pubomatic/${this.landlordId}/${this.letting.id}?skipIntroPage=true`;
    },
    pubomaticAppEditAdLink() {
      return RedirectOperations.getURL(AppRedirect.AD_EDIT, {
        landlordId: this.landlordId,
        adId: this.letting.id,
      });
    },
    prospectDetailsRouteName() {
      return 'rentalObject_prospect_details';
    },
    isAdExpired() {
      return this.letting.status.status === this.$options.LETTING_STATUS.expired;
    },
    isRentalObjectCommercial() {
      return this.letting?.rentalObject?.propertyType === 'commercial';
    },
    adQueryFilter() {
      switch (this.letting.status.status) {
        case this.$options.LETTING_STATUS.active:
          return 'active';
        case this.$options.LETTING_STATUS.expired:
          return 'expired';
        default:
          return 'all';
      }
    },
  },
  methods: {
    ...mapActions([
      'getLetting',
      'publishLetting',
      'unpublishLetting',
      'toggleNewCandidateModal',
      'getLandlordInterestManagementCandidates',
      'inviteToViewing',
      'declineProspects',
      'updateProspectScore',
    ]),

    getPublisherBadge(publisher) {
      const publisherData = this.publisherData[publisher];

      if (publisherData === undefined) return;

      const publisherName = this.$options.PUBLISHER_NAME[publisher];

      if (publisherData.status === this.$options.PUBLISHER_STATUS.expired) {
        return {
          text: `${publisherName} (utløpt)`,
          variation: 'error',
        };
      }

      if (
        publisherData.status === this.$options.PUBLISHER_STATUS.uploaded ||
        publisherData.status === this.$options.PUBLISHER_STATUS.active
      ) {
        let text;

        if (publisherData.toDate) {
          const expirationString = moment(publisherData.toDate).fromNow();
          text = `${publisherName} (utløper ${expirationString})`;
        } else {
          text = publisherName;
        }

        return {
          text,
          variation: publisher === this.$options.PUBLISHER.finn ? 'info' : 'primary',
          url: publisherData.url,
        };
      }

      return {
        text: `${publisherName} (ukjent)`,
        variation: 'error',
      };
    },

    async unpublishAd() {
      this.isUnpublishingAd = true;

      await this.unpublishLetting({
        landlordId: this.landlordId,
        lettingId: this.letting.id,
      });

      this.isUnpublishingAd = this.isUnpublishModalVisible = false;
      window.location.href = window.location.pathname;
    },
    countUnreadMessages(candidate) {
      return candidate.messages.reduce(
        (acc, message) =>
          acc +
          (message.sender !== 'landlord' && (message.last_read == null || message.date > message.last_read) ? 1 : 0),
        0
      );
    },
    async getIMCandidates() {
      try {
        await this.getLandlordInterestManagementCandidates({
          landlordId: this.landlordId,
          iMId: this.iM.id,
        });
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke hente kandidater.', 'error');
      }
    },
    updateNewViewingDetails(vw) {
      this.newViewing = vw;
    },
    inviteToViewingAndClose() {
      try {
        this.selectedCandidates.map(async candidateId => {
          await this.inviteToViewing({
            landlordId: this.landlordId,
            iMId: this.iM.id,
            prospectId: candidateId,
            viewing: this.newViewing,
          });
        });

        this.isInviteToViewingModalVisible = false;
        window.location.reload();
        this.$toast('Invitasjon sendt.', 'success');
      } catch (error) {
        console.log(error);
        this.$toast('Kunne ikke sende invitasjon', 'error');
      }
    },
    async declineAndClose() {
      try {
        await this.declineProspects({
          landlordId: this.landlordId,
          iMId: this.iM.id,
          prospects: this.selectedCandidates,
        });
        this.$toast('Avslag sendt.', 'success');
        this.getIMCandidates();
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke avslå interessenter.', 'error');
      }
      this.isDeclineModalVisible = false;
    },
    toggleAllCandidates() {
      if (this.isAllCandidatesSelected) {
        this.candidateToggling = {};
      } else {
        this.candidateToggling = this.getProspects.map(item => item.id).reduce((acc, a) => ({ ...acc, [a]: true }), []);
      }
    },
    toggleCandidate(candidateId) {
      const isChecked = this.candidateToggling[candidateId];

      this.candidateToggling = {
        ...this.candidateToggling,
        [candidateId]: !isChecked,
      };
    },
    goToProspectDetails(candidateId) {
      this.$router.push({
        name: 'rentalObject_prospect_details',
        params: {
          iMId: this.iM.id,
          prospectId: candidateId,
        },
      });
    },
    toggleCandidatesSorting(field) {
      if (this.candidatesSorting.field !== field) {
        this.candidatesSorting.isOrderDesc = true;
      } else {
        this.candidatesSorting.isOrderDesc = !this.candidatesSorting.isOrderDesc;
      }

      this.candidatesSorting.field = field;
    },
    initCreditCheckObject(creditCheck) {
      if (!creditCheck || creditCheck === this.$options.CREDIT_CHECK_STATUS.notInitiated) {
        return this.creditCheckObjects[this.$options.CREDIT_CHECK_STATUS.notInitiated];
      }

      if (creditCheck === this.$options.CREDIT_CHECK_STATUS.candidateInvited) {
        return this.creditCheckObjects[this.$options.CREDIT_CHECK_STATUS.candidateInvited];
      }

      if (creditCheck === this.$options.CREDIT_CHECK_STATUS.candidateRejected) {
        return this.creditCheckObjects[this.$options.CREDIT_CHECK_STATUS.candidateRejected];
      }

      if (creditCheck === this.$options.CREDIT_CHECK_STATUS.negative) {
        return this.creditCheckObjects[this.$options.CREDIT_CHECK_STATUS.negative];
      }

      if (creditCheck === this.$options.CREDIT_CHECK_STATUS.positive) {
        return this.creditCheckObjects[this.$options.CREDIT_CHECK_STATUS.positive];
      }

      return this.creditCheckObjects[this.$options.CREDIT_CHECK_STATUS.notInitiated];
    },
  },
  async created() {
    if (this.candidates) {
      await this.getIMCandidates();
    }
  },
  mounted() {
    setTimeout(() => {
      tippy('[data-tippy-content]');
    }, 1000);
  },
});
</script>

<style lang="scss" scoped>
.rentalObjectAd__ad {
  border: 1px solid #e9eaeb;
  border-radius: 12px;
  padding: 1.5rem;
  background: white;
}
.rentalObjectAd__ad__actionsMenu {
  display: flex;
  justify-content: flex-end;
  margin: -0.4rem -1rem 0 0;
}
.rentalObjectAd__ad__flex {
  display: flex;
  flex-direction: column;
}
.rentalObjectAd__ad__flex__imageLink {
  width: 100%;
}
.rentalObjectAd__ad__flex__imageLink img {
  border-radius: 8px;
  height: 169px;
  object-fit: cover;
  width: 100%;
}
.rentalObjectAd__ad__flex__content {
  margin-top: 1rem;
}
.rentalObjectAd__ad__flex__content__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.rentalObjectAd__ad__flex__content__publishBadges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.rentalObjectAd__ad__flex__content__buttons {
  display: flex;
  flex-direction: column;
  gap: 0.6rem 0;
  margin-top: 1.5rem;
}
.rentalObjectAd__ad__flex__content__titleRow__marketPlaceLink {
  color: unset;
  text-decoration: none;
}
.rentalObjectAd__ad__flex__content__titleRow__buttons {
  display: none;
}
.rentalObjectAd__ad__candidatesSelection {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.8rem 0rem;
}
.rentalObjectAd__ad__candidatesSelection__buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem 0rem;
  width: 100%;
}
.rentalObjectAd__ad__filterSection {
  display: none;
}
.rentalObjectAd__ad__candidateCardsSection {
  display: grid;
  margin-top: 1rem;
}
.rentalObjectAd__ad__candidateCardsSection__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem 0rem;
}
.rentalObjectAd__ad__candidateSection {
  display: none;
}
.rentalObjectAd__ad__dot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: $primary;
}
input,
select {
  font-size: 1rem;
  padding: 0.8rem 1rem;
  background-color: white;
  border: 1px solid $gray-400;
  border-radius: 8px;
  width: 100%;
  height: 48.6px;
  outline: 0;
}
input[type='checkbox'] {
  width: 20px;
  height: 20px;
}

@media (min-width: 650px) {
  .rentalObjectAd__ad {
    gap: 0 1rem;
  }
  .rentalObjectAd__ad__actionsMenu {
    display: none;
  }
  .rentalObjectAd__ad__flex {
    display: flex;
    flex-direction: row;
    gap: 0 1rem;
  }
  .rentalObjectAd__ad__flex__imageLink {
    width: 220px;
  }
  .rentalObjectAd__ad__flex__imageLink img {
    min-width: 220px;
  }
  .rentalObjectAd__ad__flex__content {
    margin-top: 0;
    width: 100%;
  }
  .rentalObjectAd__ad__flex__content__titleRow {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .rentalObjectAd__ad__flex__content__titleRow__buttons {
    display: flex;
    gap: 0 0.8rem;
    height: fit-content;
    margin-bottom: 1rem;
  }
  .rentalObjectAd__ad__flex__content__buttons {
    display: none;
  }
}

@media (min-width: 400px) {
  .rentalObjectAd__ad__filterSection {
    display: initial;
  }
  .rentalObjectAd__ad__filterSection__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem 1rem;
  }
  .rentalObjectAd__ad__candidateCardsSection {
    margin-top: 0;
  }
  .rentalObjectAd__ad__candidateCardsSection__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem 1rem;
  }
}

@media (min-width: 470px) {
  .rentalObjectAd__ad__candidatesSelection__buttons {
    display: flex;
    flex-direction: row;
    gap: 0rem 0.5rem;
    width: fit-content;
  }
}

@media (min-width: 900px) {
  .rentalObjectAd__ad__filterSection__grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 0rem 1rem;
  }
}

@media (min-width: 1360px) {
  .rentalObjectAd__ad__candidateCardsSection {
    display: none;
  }
  .rentalObjectAd__ad__candidateSection {
    display: initial;
  }
  .rentalObjectAd__ad__candidateSection__grid {
    display: grid;
    grid-template-columns: 28% 1fr 1fr 1fr 1fr 1fr;
    align-items: center;
  }
  .rentalObjectAd__ad__candidateSection__grid a {
    color: unset;
  }
  .rentalObjectAd__ad__candidateSection__grid.-header {
    height: 30px;
  }
  .rentalObjectAd__ad__candidateSection__grid.-content {
    height: 60px;
  }
  .rentalObjectAd__ad__candidateSection__grid__tag {
    width: fit-content;
  }
  .rentalObjectAd__ad__candidateSection__grid__questionMark {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 9999px;
    width: 20px;
    height: 20px;
    border: 1px solid #a4a7ae;
  }
  .rentalObjectAd__ad__candidateSection__grid__tooltipContent {
    display: grid;
    grid-template-columns: 150px 1fr;
    gap: 0rem 1rem;
    align-items: center;
  }
  .rentalObjectAd__ad__candidateSection__grid__tooltipContent__tagText {
    font-size: 0.8rem;
  }
  .rentalObjectAd__ad__candidateSection__grid__tooltipContent:not(:last-child) {
    margin-bottom: 1rem;
  }
}
</style>
